// src/pages/Utilisateurs/components/UsersPanel.jsx
import React from "react";
import {
  FaCopy,
  FaEdit,
  FaEye,
  FaInbox,
  FaTrash,
  FaTrophy,
  FaUser,
} from "react-icons/fa";

import {
  formatDateTimeFR,
  getRoleLabel,
  getRoleTone,
  getUserCreatedDate,
  getUserId,
  getUserName,
} from "../usersAdminUtils";

export default function UsersPanel({
  rows,
  loading,
  searchTerm,
  rolesMap,
  roleOptions,
  competitionsMap,
  savingRoleId,
  deletingId,
  onEdit,
  onDelete,
  onRoleChange,
  onOpenCompetition,
}) {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-4 dark:border-slate-800 sm:p-5">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">
          Liste des utilisateurs
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {rows.length} utilisateur{rows.length > 1 ? "s" : ""} affiché
          {rows.length > 1 ? "s" : ""}.
        </p>
      </div>

      {loading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState searchTerm={searchTerm} />
      ) : (
        <>
          <UsersTable
            rows={rows}
            rolesMap={rolesMap}
            roleOptions={roleOptions}
            competitionsMap={competitionsMap}
            savingRoleId={savingRoleId}
            deletingId={deletingId}
            onEdit={onEdit}
            onDelete={onDelete}
            onRoleChange={onRoleChange}
            onOpenCompetition={onOpenCompetition}
          />

          <div className="grid grid-cols-1 gap-3 p-3 lg:hidden">
            {rows.map((user) => (
              <UserCard
                key={getUserId(user)}
                user={user}
                rolesMap={rolesMap}
                roleOptions={roleOptions}
                competitions={competitionsMap[getUserId(user)] || []}
                savingRoleId={savingRoleId}
                deletingId={deletingId}
                onEdit={onEdit}
                onDelete={onDelete}
                onRoleChange={onRoleChange}
                onOpenCompetition={onOpenCompetition}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function UsersTable({
  rows,
  rolesMap,
  roleOptions,
  competitionsMap,
  savingRoleId,
  deletingId,
  onEdit,
  onDelete,
  onRoleChange,
  onOpenCompetition,
}) {
  return (
    <div className="hidden overflow-x-auto lg:block">
      <table className="min-w-[1050px] w-full table-fixed text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
          <tr>
            <th className="w-[320px] px-5 py-4">Utilisateur</th>
            <th className="w-[260px] px-5 py-4">Contact</th>
            <th className="w-[180px] px-5 py-4">Rôle / Formule</th>
            <th className="w-[240px] px-5 py-4">Compétitions</th>
            <th className="w-[170px] px-5 py-4 text-right">Actions</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((user) => {
            const id = getUserId(user);
            const competitions = competitionsMap[id] || [];

            return (
              <tr
                key={id}
                className="transition hover:bg-slate-50/80 dark:hover:bg-slate-800/50"
              >
                <td className="px-5 py-4 align-top">
                  <div className="flex items-start gap-3">
                    <UserAvatar user={user} />

                    <div className="min-w-0">
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
                        #{id}
                      </div>

                      <div className="mt-1 font-bold text-slate-900 dark:text-white">
                        {getUserName(user)}
                      </div>

                      <div className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                        Inscrit : {formatDateTimeFR(getUserCreatedDate(user))}
                      </div>
                    </div>
                  </div>
                </td>

                <td className="px-5 py-4 align-top">
                  <EmailCopy email={user.email} />
                </td>

                <td className="px-5 py-4 align-top">
                  <div className="space-y-2">
                    <RoleBadge roleId={user.role_id} rolesMap={rolesMap} />

                    <select
                      value={user.role_id || ""}
                      onChange={(event) => onRoleChange(user, event.target.value)}
                      disabled={savingRoleId === id}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-indigo-950"
                    >
                      {roleOptions.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </td>

                <td className="px-5 py-4 align-top">
                  <CompetitionsList
                    competitions={competitions}
                    onOpenCompetition={onOpenCompetition}
                  />
                </td>

                <td className="px-5 py-4 text-right align-top">
                  <div className="inline-flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(user)}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-3.5 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
                    >
                      <FaEdit />
                      Éditer
                    </button>

                    <button
                      type="button"
                      onClick={() => onDelete(user)}
                      disabled={deletingId === id}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-3.5 py-2 text-sm font-bold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UserCard({
  user,
  rolesMap,
  roleOptions,
  competitions,
  savingRoleId,
  deletingId,
  onEdit,
  onDelete,
  onRoleChange,
  onOpenCompetition,
}) {
  const id = getUserId(user);

  return (
    <article className="rounded-[26px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        <UserAvatar user={user} />

        <div className="min-w-0 flex-1">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
            #{id}
          </div>

          <h3 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            {getUserName(user)}
          </h3>

          <EmailCopy email={user.email} />
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/50">
        <div className="mb-2">
          <RoleBadge roleId={user.role_id} rolesMap={rolesMap} />
        </div>

        <select
          value={user.role_id || ""}
          onChange={(event) => onRoleChange(user, event.target.value)}
          disabled={savingRoleId === id}
          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
        >
          {roleOptions.map((role) => (
            <option key={role.id} value={role.id}>
              {role.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <CompetitionsList
          competitions={competitions}
          onOpenCompetition={onOpenCompetition}
        />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onEdit(user)}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-3 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950"
        >
          <FaEdit />
          Éditer
        </button>

        <button
          type="button"
          onClick={() => onDelete(user)}
          disabled={deletingId === id}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-3 py-2.5 text-sm font-bold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
        >
          <FaTrash />
          Supprimer
        </button>
      </div>
    </article>
  );
}

function UserAvatar({ user }) {
  const src = user?.photoURL || user?.photo_url || "/img/user_avatar.jpg";

  return (
    <img
      src={src}
      alt={getUserName(user)}
      onError={(event) => {
        event.currentTarget.src = "/img/user_avatar.jpg";
      }}
      className="h-12 w-12 shrink-0 rounded-2xl bg-slate-100 object-cover dark:bg-slate-800"
    />
  );
}

function RoleBadge({ roleId, rolesMap }) {
  return (
    <span
      className={`inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${getRoleTone(
        roleId
      )}`}
    >
      {getRoleLabel(roleId, rolesMap)}
    </span>
  );
}

function EmailCopy({ email }) {
  const copyEmail = async () => {
    if (!email) return;

    try {
      await navigator.clipboard.writeText(email);
    } catch {
      console.warn("Impossible de copier l'email");
    }
  };

  if (!email) {
    return (
      <div className="text-sm font-semibold text-slate-500">
        Email non renseigné
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="break-all text-sm font-semibold text-slate-600 dark:text-slate-300">
        {email}
      </span>

      <button
        type="button"
        onClick={copyEmail}
        className="inline-flex items-center gap-1 rounded-xl bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
      >
        <FaCopy />
        Copier
      </button>
    </div>
  );
}

function CompetitionsList({ competitions, onOpenCompetition }) {
  if (!competitions.length) {
    return (
      <div className="inline-flex rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        Aucune compétition
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {competitions.slice(0, 3).map((competition) => (
        <button
          key={competition.id}
          type="button"
          onClick={() => onOpenCompetition(competition)}
          className="inline-flex w-fit items-center gap-2 rounded-2xl bg-indigo-50 px-3 py-2 text-left text-xs font-bold text-indigo-700 transition hover:bg-indigo-100 dark:bg-indigo-950/60 dark:text-indigo-300 dark:hover:bg-indigo-950"
        >
          <FaTrophy />
          <span className="max-w-[220px] truncate">
            {competition.nom_competition || `Compétition #${competition.id}`}
          </span>
          <FaEye />
        </button>
      ))}

      {competitions.length > 3 ? (
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
          +{competitions.length - 3} autre
          {competitions.length - 3 > 1 ? "s" : ""}
        </span>
      ) : null}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-3 p-4 sm:p-5">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="flex animate-pulse items-center gap-3 rounded-[24px] border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50"
        >
          <div className="h-12 w-12 rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-3/4 rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-3 w-1/2 rounded-full bg-slate-200 dark:bg-slate-800" />
          </div>
          <div className="hidden h-9 w-28 rounded-2xl bg-slate-200 dark:bg-slate-800 sm:block" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ searchTerm }) {
  return (
    <div className="flex flex-col items-center justify-center px-5 py-16 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-[24px] bg-slate-100 text-2xl text-slate-500 dark:bg-slate-800 dark:text-slate-300">
        <FaInbox />
      </div>

      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
        {searchTerm ? "Aucun utilisateur trouvé" : "Aucun utilisateur"}
      </h3>

      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500 dark:text-slate-400">
        {searchTerm
          ? "Essayez avec un autre mot-clé ou réinitialisez les filtres."
          : "Les utilisateurs créés apparaîtront ici."}
      </p>
    </div>
  );
}